import React, { useCallback, useMemo, useState } from 'react'
import { BannerContext } from './banner-context'
import { Banner, BannerKey } from '../types'

interface Props {
  children?: React.ReactNode
}

/**
 * This provider has the array of banners to show and the functions to remove or add banners
 */
const BannerProvider: React.FC<Props> = ({ children }) => {
  const [warningBanners, setWarningBanners] = useState<Banner[]>([])
  const [infoBanners, setInfoBanners] = useState<Banner[]>([])

  const addWarningBanners = useCallback((banners: Banner[]) => {
    setWarningBanners(banners)
  }, [])

  const addInfoBanners = useCallback((banners: Banner[]) => {
    setInfoBanners(banners)
  }, [])

  const removeBanner = useCallback((key: BannerKey) => {
    setWarningBanners(prevBanners => prevBanners.filter(banner => banner.key !== key))
    setInfoBanners(prevBanners => prevBanners.filter(banner => banner.key !== key))
  }, [])

  const clearBanners = useCallback(() => {
    setWarningBanners([])
    setInfoBanners([])
  }, [])

  const addBanner = useCallback((newBanner: Banner) => {
    const setBanners = newBanner.type === 'warning' ? setWarningBanners : setInfoBanners

    setBanners(prevBanners => {
      const newBannerAlreadyAdded = prevBanners.some(prevBanner => prevBanner.key === newBanner.key)

      if (newBannerAlreadyAdded) {
        return prevBanners
      }

      return [...prevBanners, newBanner]
    })
  }, [])

  const value = useMemo(
    () => ({
      warningBanners,
      infoBanners,
      addWarningBanners,
      addInfoBanners,
      removeBanner,
      addBanner,
      clearBanners,
    }),
    [warningBanners, infoBanners, addWarningBanners, addInfoBanners, removeBanner, addBanner, clearBanners]
  )

  return <BannerContext.Provider value={value}>{children}</BannerContext.Provider>
}

export default BannerProvider
